/**
 * BrandLogo — renders the Marca logo from the brand-logo store.
 *
 * Returns null (nothing) when no logo URL is set, so callers never need to
 * guard against a missing logo themselves.
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import { getBrandLogoUrl, subscribe } from "@/shared/lib/brand-logo.js";
import { averageLuminance } from "@/shared/lib/image-luminance.js";

interface BrandLogoProps {
  /** Logo height in px. Width auto-scales. Default: 28. */
  height?: number;
  className?: string;
}

/** Logos brighter than this would wash out on a light surface → need a plate. */
const LIGHT_LOGO_THRESHOLD = 0.6;

export function BrandLogo({ height = 28, className }: BrandLogoProps) {
  const url = useSyncExternalStore(subscribe, getBrandLogoUrl, () => null);
  // A light logo gets a dark backdrop plate so it stays visible on the app's
  // light surfaces (técnico header, login, sidebar). A dark logo renders raw —
  // it already contrasts the light surface, and a plate would just add a box.
  const [needsPlate, setNeedsPlate] = useState(false);

  useEffect(() => {
    let alive = true;
    setNeedsPlate(false);
    if (!url) return;
    averageLuminance(url).then((lum) => {
      if (alive && lum != null) setNeedsPlate(lum > LIGHT_LOGO_THRESHOLD);
    });
    return () => {
      alive = false;
    };
  }, [url]);

  if (!url) return null;

  const img = (
    <img src={url} alt="" style={{ height, width: "auto", display: "block" }} className={className} />
  );

  if (!needsPlate) return img;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: "#1a1a1a",
        padding: `${Math.round(height * 0.22)}px ${Math.round(height * 0.4)}px`,
        borderRadius: 8,
      }}
    >
      {img}
    </span>
  );
}
